import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/youtube-demand-gen")({
  head: () => {
    const h = pageHead("pmYoutubeDemandGen", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "YouTube Ads Agency",
          description: "YouTube TrueView, Demand Gen, Discovery Ads — managed for economic contribution in integrated funnel, not view count.",
          url: "/en/services/performance-marketing/youtube-demand-gen",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "YouTube & Demand Gen", path: "/en/services/performance-marketing/youtube-demand-gen" },
        ])),
      ],
    };
  },
  component: YouTubeEN,
});

const steps = [
  { num: "01", title: "Analysis & Asset Check (3–5 days)", body: "Account audit (if YouTube activity is already running), inventory asset analysis (which videos exist, what's missing), funnel architecture check (where does YouTube fit in the mix), competitive analysis. Output: concrete hypotheses plus realistic asset production briefing." },
  { num: "02", title: "Setup, Tracking & First Asset Variations (1–2 weeks)", body: "View-through conversion setup, server-side tracking hardening. Account structure aligned to funnel stages. First TrueView campaigns with existing assets or picazzoo variations. Demand Gen setup for mid-funnel reactivation." },
  { num: "03", title: "Iteration & Format Mix Optimization (from week 2–3)", body: "Iteration on hook variation in first 5 seconds (crucial for TrueView), format mix (Bumper, TrueView, Discovery), audience sharpness. Weekly iteration on view-through performance plus assisted conversions." },
  { num: "04", title: "Scaling & Integrated Reporting (continuous)", body: "Budget scaling in working audience-asset combinations. Cross-channel reporting in search and direct traffic context — making YouTube impact visible where it actually happens. Operational management typically directly by the managing director." },
];

const capabilities = [
  { name: "TrueView In-Stream & In-Feed", detail: "Hook tests in 5 seconds" },
  { name: "Bumper Ads", detail: "6-second reach format" },
  { name: "Demand Gen Campaigns", detail: "YouTube · Discover · Gmail" },
  { name: "Discovery Ads", detail: "Native YouTube discovery" },
  { name: "View-Through Conversion Setup", detail: "Multi-week tracking chain" },
  { name: "Asset Production with picazzoo", detail: "AI video variations" },
  { name: "Audience Strategies", detail: "Custom Affinity · In-Market" },
  { name: "Cross-Channel Reporting", detail: "Search + Direct + YouTube" },
];

const audience = [
  { tag: "Primary", title: "B2C brands in build-up", body: "YouTube is often the most economical awareness channel for young B2C brands — low CPMs for high-quality reach, plus the ability to tell brand stories via video. Sensible from monthly budgets of around €5,000 for YouTube alone." },
  { tag: "Secondary", title: "Established e-commerce with awareness need", body: "For e-commerce setups that have exhausted search volume and want to generate mid-funnel demand. Demand Gen plus TrueView as search complement. Works particularly well for visual product categories." },
  { tag: "Tertiary", title: "SaaS Top Funnel", body: "YouTube works for SaaS in awareness phase — demo videos, use-case stories, product tours as TrueView. Bottom-funnel conversion typically stays with Search." },
  { tag: "Disqualification", title: "When we're not the right partner", body: "For monthly budgets under €3,000 for YouTube. When the expectation is “viral YouTube hits”. When no video assets are available and none are to be produced." },
];

const realistic = [
  "CPM values in DACH market between €4 and €12 for high-quality reach — depending on audience sharpness and format. Demand Gen often lower than TrueView.",
  "View-through conversion lift of 15–30% on entire funnel performance is a typical range when YouTube is systematically deployed in mid-funnel.",
  "Search volume leverage of 10–25% through YouTube awareness investments — demand generation effect measurable two to four weeks later in branded and non-branded search.",
  "Asset production efficiency of 3–5x through picazzoo pipeline vs. classic designer production. This makes YouTube economically viable for mandates where asset costs would otherwise be the blocker.",
];

const value = [
  ["Awareness-performance bridge instead of brand vanity.", "We report on view-through conversions, assisted conversions, and cross-channel impact — not on view count."],
  ["Asset production scalable via picazzoo.", "Video asset bottleneck becomes a Calvarius strength."],
  ["Integrated funnel reporting.", "YouTube impact visible where it actually happens (Search, Direct, later)."],
  ["Format mix discipline.", "TrueView, Bumper, Demand Gen, Discovery — fitting funnel stage, not copy-paste."],
  ["Operational management by experienced hand.", "Directly by the managing director in most mandates."],
  ["Compensation model without budget coupling.", "We don't earn more when you spend more."],
];

const related = [
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "YouTube runs technically via Google Ads — integrated management mandatory" },
  { to: "/en/services/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Complementary video performance channel with different funnel logic" },
  { to: "/en/services/conversion-optimization", name: "Conversion & Web Optimization", sub: "Awareness without conversion floor is burned money" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/meta-ads", name: "Advertising on Facebook & Instagram", sub: "Performance on Instagram and Facebook with creative discipline." },
  { to: "/en/services/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Performance on TikTok beyond the influencer logic." },
];

function YouTubeEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · YouTube & Demand Gen"
          headline={<>YouTube Ads Agency — <em>Awareness with Performance Logic,</em> Not Brand Vanity.</>}
          lead="YouTube TrueView, Demand Gen, Discovery Ads — managed for economic contribution in integrated funnel, not view count. Calvarius works as a YouTube Ads agency for B2C brands in build-up, established e-commerce setups with awareness needs, and SaaS providers in top funnel."
          metaStrip={["Awareness-Performance Bridge", "View-Through Management", "Integrated in Google Ads"]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "YouTube & Demand Gen" },
          ]}
        />

        <Section number="02" kicker="Position" headline={<>What a YouTube Ads Agency with <em>performance focus</em> delivers today.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for a YouTube Ads agency with performance focus has typically experienced a difficult tension: YouTube is simultaneously an awareness channel (with view counts and brand lift as default KPIs) and a performance channel (with view-through conversions and direct response possibilities). Anyone running YouTube as pure brand awareness channel leaves conversion leverage on the table. Anyone running YouTube as pure direct response channel misunderstands the medium's strengths. But anyone connecting awareness with performance logic enters the zone where YouTube becomes economically viable.</p>
              <p className="mb-5">Calvarius works with YouTube and Demand Gen as an <strong className="font-semibold text-ink">awareness-performance bridge</strong>. That means: we deploy TrueView and Discovery Ads where economically sensible — not everywhere. Demand Gen complements classic search campaigns in mid-funnel, where demand generation happens before anyone actively searches. We manage on view-through conversions plus assisted conversions, not on view count or CPV.</p>
              <p>Concretely: through <Link to="/en/services/picazzoo" className="underline">picazzoo</Link> we produce video asset variations in brand voice where designer capacity isn't enough. We use YouTube-specific format logic — Bumper Ads for reach, TrueView In-Stream for engagement, Demand Gen for mid-funnel reactivation. And we manage cross-channel impact — YouTube investments often don't show in the YouTube account but in search and direct traffic effects two to four weeks later.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience Fit" headline={<>When you need a YouTube Ads Agency <em>— and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our YouTube Ads Agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic video agencies often need eight to twelve weeks for setup plus asset production, we are productive within two to three weeks — possible through picazzoo as asset pipeline and integrated account setup instead of isolated video workshop.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our YouTube Ads Agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>YouTube management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Platform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Google Ads UI (YouTube)</li>
                  <li>YouTube Studio</li>
                  <li>Brand Lift Studies (selectively)</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>View-through conversion tracking</li>
                  <li>Server-side via GTM Server</li>
                  <li>GA4 cross-channel validation</li>
                  <li>Looker Studio funnel reporting</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/en/services/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">AI video asset production in brand voice</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of <em>YouTube Ads optimization.</em></>}
          caption="YouTube impact is multi-week cumulative — anyone optimizing on weekly KPIs doesn't see the value.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>YouTube Ads Agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Get concrete">
          <CTABlock
            headline={<>YouTube Ads audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation, we clarify whether YouTube Ads makes economic sense in your funnel setup — as awareness-performance bridge or mid-funnel reactivation. You get an honest assessment."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
